/**
 * Instance branding at runtime (DESIGN.md §16): the apps load it from the platform API
 * (`PlatformRuntime`, implemented by @platform/api-client's public client) and apply it without a
 * rebuild: brand tokens, instance name, logos, favicon, document title, user defaults.
 */
import { BUILTIN_BRANDING, instanceNameFor, type PublicBranding } from '@platform/branding';
import { useI18n } from '@platform/i18n';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { usePreferences } from '../hooks/use-preferences';
import { applyBrandTheme, applyFavicon } from '../lib/brand';
import { resolveTheme } from '../lib/preferences';
import { cn } from '../lib/utils';

/** Public instance data an app loads before login (matches `createPublicClient` of @platform/api-client). */
export interface PlatformRuntime {
  branding(): Promise<PublicBranding>;
  languages(): Promise<{ defaultLocale: string; languages: readonly { locale: string; name: string; hash: string }[] }>;
  messages(locale: string): Promise<{ hash: string; messages: Readonly<Record<string, string>> }>;
  /** Absolute URL of an API path (branding images). */
  url(path: string): string;
}

export interface BrandingContextValue {
  /** Effective branding; null until known (built-in look). */
  branding: PublicBranding | null;
  /** Replaces the branding after an admin change (applies at once, no reload). */
  setBranding: (branding: PublicBranding) => void;
  /** Reloads the enabled languages and their runtime messages (after a pack change). */
  reloadLanguages: () => void;
  /** URL of a branding image path from the API. */
  assetUrl: (path: string) => string;
}

export const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  setBranding: () => undefined,
  reloadLanguages: () => undefined,
  assetUrl: (path) => path,
});

export interface Branding extends BrandingContextValue {
  /** Instance name in the current language. */
  instanceName: string;
  /** Logo for the current theme (dark falls back to the light logo); undefined = none. */
  logoUrl: string | undefined;
  faviconUrl: string | undefined;
  backgroundUrl: string | undefined;
}

/** Effective branding for the current language and theme. */
export function useBranding(): Branding {
  const context = useContext(BrandingContext);
  const { locale } = useI18n();
  const { theme } = usePreferences();
  const { branding, assetUrl } = context;
  return useMemo(() => {
    const assets = branding?.assets;
    const url = (asset: { url: string } | null | undefined) => (asset ? assetUrl(asset.url) : undefined);
    const dark = resolveTheme(theme) === 'dark';
    return {
      ...context,
      instanceName: instanceNameFor(branding?.settings ?? BUILTIN_BRANDING, locale),
      logoUrl: url((dark ? assets?.logoDark : undefined) ?? assets?.logoLight),
      faviconUrl: url(assets?.favicon ?? assets?.logoLight),
      backgroundUrl: url(assets?.loginBackground),
    };
  }, [context, branding, assetUrl, locale, theme]);
}

/** Applies brand tokens, favicon and the document title (`<app name> · <instance name>`). */
export function BrandingEffects({ appName }: { appName: string }) {
  const { branding, instanceName, faviconUrl } = useBranding();
  const theme = branding?.theme ?? null;
  useEffect(() => applyBrandTheme(theme), [theme]);
  useEffect(() => applyFavicon(faviconUrl ?? null), [faviconUrl]);
  useEffect(() => {
    document.title = `${appName} · ${instanceName}`;
  }, [appName, instanceName]);
  return null;
}

export interface BrandLogoProps {
  /** Shown when no logo is uploaded (an icon). */
  fallback: ReactNode;
  /** `header` = TopBar size, `card` = sign-in card size. */
  size?: 'header' | 'card';
  className?: string;
}

/** The uploaded logo for the current theme, or the fallback icon. Alt text = instance name. */
export function BrandLogo({ fallback, size = 'header', className }: BrandLogoProps) {
  const { logoUrl, instanceName } = useBranding();
  if (!logoUrl) return <>{fallback}</>;
  return (
    <img
      src={logoUrl}
      alt={instanceName}
      className={cn('block w-auto object-contain', size === 'header' ? 'h-6 max-w-32' : 'h-10 max-w-48', className)}
    />
  );
}
